import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { FormDrawer } from '../../../forms';
import financeFundraisingAPI from '../../../../services/financeFundraisingAPI';
import { getInvestmentStageOptions } from '../../../placeholders/financingCatalogDefaults';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatPct = (value) => {
  const n = Number(value || 0);
  if (!n) return '—';
  return `${n.toLocaleString('es-CL', { maximumFractionDigits: 4 })}%`;
};

const EMPTY_ROUND = {
  name: '',
  stage: '',
  status: 'Planeada',
  target_amount: '',
  raised_amount: '',
  pre_money: '',
  post_money: '',
  open_date: '',
  close_date: '',
  notes: '',
};

const EMPTY_INVESTMENT = {
  investor_id: '',
  round_id: '',
  amount: '',
  instrument: 'Equity',
  share_class: '',
  ownership_pct: '',
  investment_date: '',
  status: 'Comprometido',
  notes: '',
  sync_investor: true,
};

const EMPTY_CAP = {
  shareholder_name: '',
  investor_id: '',
  round_id: '',
  share_class: 'Common',
  shares: '',
  ownership_pct: '',
  fully_diluted_pct: '',
  notes: '',
  status: 'Activo',
};

const ROUND_STATUS = [
  { value: 'Planeada', label: 'Planeada' },
  { value: 'Abierta', label: 'Abierta' },
  { value: 'Cerrada', label: 'Cerrada' },
  { value: 'Cancelada', label: 'Cancelada' },
];

const INVESTMENT_STATUS = [
  { value: 'Comprometido', label: 'Comprometido' },
  { value: 'Cerrado', label: 'Cerrado' },
  { value: 'Cancelado', label: 'Cancelado' },
];

const INSTRUMENTS = [
  { value: 'Equity', label: 'Equity' },
  { value: 'SAFE', label: 'SAFE' },
  { value: 'Convertible', label: 'Convertible note' },
  { value: 'Debt', label: 'Deuda' },
  { value: 'Otro', label: 'Otro' },
];

const SHARE_CLASSES = [
  { value: 'Common', label: 'Common' },
  { value: 'Preferred', label: 'Preferred' },
  { value: 'Options', label: 'Options / pool' },
  { value: 'Otro', label: 'Otro' },
];

const btnPrimary = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 36,
  padding: '0 14px',
  borderRadius: 8,
  border: 'none',
  background: '#16a34a',
  color: '#fff',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
};

const btnSecondary = (ui, isDarkMode) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 36,
  padding: '0 12px',
  borderRadius: 8,
  border: `1px solid ${ui.inputBorder}`,
  background: isDarkMode ? '#27272a' : '#fff',
  color: ui.text,
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
});

const SimpleTable = ({
  columns,
  rows,
  loading,
  error,
  isDarkMode,
  ui,
  search,
  onSearch,
  selectedIds,
  onToggleSelect,
  onRowClick,
  showSearch = true,
}) => (
  <div>
    {showSearch ? (
      <div style={{ padding: '10px 12px', borderBottom: `1px solid ${ui.border}` }}>
        <input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Buscar…"
          style={{
            width: '100%',
            height: 34,
            borderRadius: 8,
            border: `1px solid ${ui.inputBorder}`,
            background: isDarkMode ? '#18181b' : '#fff',
            color: ui.text,
            padding: '0 10px',
            fontSize: 13,
          }}
        />
      </div>
    ) : null}
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: isDarkMode ? '#18181b' : '#f8fafc' }}>
            {onToggleSelect ? <th style={{ width: 36, padding: '8px 10px' }} /> : null}
            {columns.map((col) => (
              <th
                key={col.key}
                style={{
                  textAlign: 'left',
                  padding: '8px 10px',
                  color: ui.muted,
                  fontWeight: 600,
                  borderBottom: `1px solid ${ui.border}`,
                  whiteSpace: 'nowrap',
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length + (onToggleSelect ? 1 : 0)} style={{ padding: 16, color: ui.muted }}>
                Cargando…
              </td>
            </tr>
          ) : error ? (
            <tr>
              <td colSpan={columns.length + (onToggleSelect ? 1 : 0)} style={{ padding: 16, color: '#dc2626' }}>
                {error}
              </td>
            </tr>
          ) : !rows.length ? (
            <tr>
              <td colSpan={columns.length + (onToggleSelect ? 1 : 0)} style={{ padding: 16, color: ui.muted }}>
                Sin registros.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => onRowClick?.(row)}
                style={{ cursor: onRowClick ? 'pointer' : 'default', borderBottom: `1px solid ${ui.border}` }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode ? '#18181b' : '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {onToggleSelect ? (
                  <td style={{ padding: '8px 10px' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      onChange={(e) => {
                        if (e.target.checked) onToggleSelect([...selectedIds, row.id]);
                        else onToggleSelect(selectedIds.filter((id) => id !== row.id));
                      }}
                    />
                  </td>
                ) : null}
                {columns.map((col) => (
                  <td key={col.key} style={{ padding: '8px 10px', color: ui.text }}>
                    {col.render ? col.render(row) : row[col.key] || '—'}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export const useFinancingFundraising = ({ enabled = true, onDataMutated } = {}) => {
  const [rounds, setRounds] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [capTable, setCapTable] = useState([]);
  const [capSummary, setCapSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadAll = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError('');
    try {
      const [r, i, c, s] = await Promise.all([
        financeFundraisingAPI.getRounds(),
        financeFundraisingAPI.getInvestments(),
        financeFundraisingAPI.getCapTable(),
        financeFundraisingAPI.getCapTableSummary(),
      ]);
      setRounds(Array.isArray(r) ? r : []);
      setInvestments(Array.isArray(i) ? i : []);
      setCapTable(Array.isArray(c) ? c : []);
      setCapSummary(s || null);
    } catch (err) {
      setError(err?.message || 'No se pudieron cargar rondas / inversiones / cap table.');
      setRounds([]);
      setInvestments([]);
      setCapTable([]);
      setCapSummary(null);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const reloadAndNotify = useCallback(async () => {
    await loadAll();
    onDataMutated?.();
  }, [loadAll, onDataMutated]);

  return {
    rounds,
    investments,
    capTable,
    capSummary,
    loading,
    error,
    reloadAndNotify,
  };
};

const FinancingFundraisingViews = ({
  tab = 'rounds',
  investors = [],
  isDarkMode = false,
  ui,
  onDataMutated,
  fundraising,
}) => {
  const stageOptions = useMemo(() => getInvestmentStageOptions(), []);
  const data = fundraising;
  const { rounds, investments, capTable, capSummary, loading, error, reloadAndNotify } = data;

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSearch('');
    setSelectedIds([]);
    setShowForm(false);
    setEditingId(null);
    setFormError('');
  }, [tab]);

  const investorOptions = useMemo(
    () => [
      { value: '', label: tab === 'cap-table' ? '(sin inversionista)' : 'Seleccionar…' },
      ...investors.map((e) => ({ value: String(e.id), label: e.name })),
    ],
    [investors, tab]
  );

  const roundOptions = useMemo(
    () => [
      { value: '', label: '(sin ronda)' },
      ...rounds.map((r) => ({
        value: String(r.id),
        label: r.stage ? `${r.name} (${r.stage})` : r.name,
      })),
    ],
    [rounds]
  );

  const filteredRounds = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rounds;
    return rounds.filter((r) =>
      [r.round_code, r.name, r.stage, r.status].join(' ').toLowerCase().includes(q)
    );
  }, [rounds, search]);

  const filteredInvestments = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return investments;
    return investments.filter((r) =>
      [r.investment_code, r.investor_name, r.round_name, r.instrument, r.status]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [investments, search]);

  const filteredCap = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return capTable;
    return capTable.filter((r) =>
      [r.entry_code, r.shareholder_name, r.investor_name, r.share_class, r.round_name]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [capTable, search]);

  const openCreate = () => {
    setEditingId(null);
    setFormError('');
    if (tab === 'rounds') setForm({ ...EMPTY_ROUND });
    else if (tab === 'investments') setForm({ ...EMPTY_INVESTMENT });
    else setForm({ ...EMPTY_CAP });
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setFormError('');
    if (tab === 'rounds') {
      setForm({
        name: row.name || '',
        stage: row.stage || '',
        status: row.status || 'Planeada',
        target_amount: row.target_amount ?? '',
        raised_amount: row.raised_amount ?? '',
        pre_money: row.pre_money ?? '',
        post_money: row.post_money ?? '',
        open_date: row.open_date ? String(row.open_date).slice(0, 10) : '',
        close_date: row.close_date ? String(row.close_date).slice(0, 10) : '',
        notes: row.notes || '',
      });
    } else if (tab === 'investments') {
      setForm({
        investor_id: row.investor_id ? String(row.investor_id) : '',
        round_id: row.round_id ? String(row.round_id) : '',
        amount: row.amount ?? '',
        instrument: row.instrument || 'Equity',
        share_class: row.share_class || '',
        ownership_pct: row.ownership_pct ?? '',
        investment_date: row.investment_date ? String(row.investment_date).slice(0, 10) : '',
        status: row.status || 'Comprometido',
        notes: row.notes || '',
        sync_investor: true,
      });
    } else {
      setForm({
        shareholder_name: row.shareholder_name || '',
        investor_id: row.investor_id ? String(row.investor_id) : '',
        round_id: row.round_id ? String(row.round_id) : '',
        share_class: row.share_class || 'Common',
        shares: row.shares ?? '',
        ownership_pct: row.ownership_pct ?? '',
        fully_diluted_pct: row.fully_diluted_pct ?? '',
        notes: row.notes || '',
        status: row.status || 'Activo',
      });
    }
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    setFormError('');
    try {
      if (tab === 'rounds') {
        if (!String(form.name || '').trim()) {
          setFormError('El nombre es obligatorio.');
          setSaving(false);
          return;
        }
        const payload = {
          ...form,
          target_amount: Number(form.target_amount || 0),
          raised_amount: Number(form.raised_amount || 0),
          pre_money: Number(form.pre_money || 0),
          post_money: Number(form.post_money || 0),
          open_date: form.open_date || null,
          close_date: form.close_date || null,
        };
        if (editingId) await financeFundraisingAPI.updateRound(editingId, payload);
        else await financeFundraisingAPI.createRound(payload);
      } else if (tab === 'investments') {
        if (!Number(form.investor_id)) {
          setFormError('Debes seleccionar un inversionista.');
          setSaving(false);
          return;
        }
        const payload = {
          ...form,
          investor_id: Number(form.investor_id),
          round_id: form.round_id ? Number(form.round_id) : null,
          amount: Number(form.amount || 0),
          ownership_pct: Number(form.ownership_pct || 0),
          investment_date: form.investment_date || null,
          sync_investor: true,
        };
        if (editingId) await financeFundraisingAPI.updateInvestment(editingId, payload);
        else await financeFundraisingAPI.createInvestment(payload);
      } else {
        if (!String(form.shareholder_name || '').trim()) {
          setFormError('El accionista es obligatorio.');
          setSaving(false);
          return;
        }
        const payload = {
          ...form,
          investor_id: form.investor_id ? Number(form.investor_id) : null,
          round_id: form.round_id ? Number(form.round_id) : null,
          shares: Number(form.shares || 0),
          ownership_pct: Number(form.ownership_pct || 0),
          fully_diluted_pct: Number(form.fully_diluted_pct || 0),
        };
        if (editingId) await financeFundraisingAPI.updateCapEntry(editingId, payload);
        else await financeFundraisingAPI.createCapEntry(payload);
      }
      setShowForm(false);
      await reloadAndNotify();
      await onDataMutated?.();
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  const bulkDelete = async () => {
    if (!selectedIds.length) return;
    const noun =
      tab === 'rounds' ? 'ronda(s)' : tab === 'investments' ? 'inversión(es)' : 'entrada(s)';
    if (!window.confirm(`¿Eliminar ${selectedIds.length} ${noun}?`)) return;
    if (tab === 'rounds') await financeFundraisingAPI.bulkDeleteRounds(selectedIds);
    else if (tab === 'investments') await financeFundraisingAPI.bulkDeleteInvestments(selectedIds);
    else await financeFundraisingAPI.bulkDeleteCapEntries(selectedIds);
    setSelectedIds([]);
    await reloadAndNotify();
    await onDataMutated?.();
  };

  const roundSections = [
    {
      key: 'basic',
      title: 'Ronda',
      fields: [
        { key: 'name', label: 'Nombre' },
        {
          key: 'stage',
          label: 'Etapa',
          type: 'select',
          options: [{ value: '', label: 'Seleccionar…' }, ...stageOptions],
        },
        { key: 'status', label: 'Estado', type: 'select', options: ROUND_STATUS },
        { key: 'target_amount', label: 'Monto objetivo (CLP)', type: 'number' },
        { key: 'raised_amount', label: 'Monto levantado (CLP)', type: 'number' },
        { key: 'pre_money', label: 'Pre-money (CLP)', type: 'number' },
        { key: 'post_money', label: 'Post-money (CLP)', type: 'number' },
        { key: 'open_date', label: 'Apertura', type: 'date' },
        { key: 'close_date', label: 'Cierre', type: 'date' },
        { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true },
      ],
    },
  ];

  const investmentSections = [
    {
      key: 'basic',
      title: 'Inversión',
      fields: [
        { key: 'investor_id', label: 'Inversionista', type: 'select', options: investorOptions },
        { key: 'round_id', label: 'Ronda', type: 'select', options: roundOptions },
        { key: 'amount', label: 'Monto (CLP)', type: 'number' },
        { key: 'instrument', label: 'Instrumento', type: 'select', options: INSTRUMENTS },
        { key: 'share_class', label: 'Clase de acción', type: 'select', options: SHARE_CLASSES },
        { key: 'ownership_pct', label: '% ownership', type: 'number' },
        { key: 'investment_date', label: 'Fecha', type: 'date' },
        { key: 'status', label: 'Estado', type: 'select', options: INVESTMENT_STATUS },
        { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true },
      ],
    },
  ];

  const capSections = [
    {
      key: 'basic',
      title: 'Cap table',
      fields: [
        { key: 'shareholder_name', label: 'Accionista' },
        { key: 'investor_id', label: 'Inversionista (opcional)', type: 'select', options: investorOptions },
        { key: 'round_id', label: 'Ronda (opcional)', type: 'select', options: roundOptions },
        { key: 'share_class', label: 'Clase', type: 'select', options: SHARE_CLASSES },
        { key: 'shares', label: 'Acciones / unidades', type: 'number' },
        { key: 'ownership_pct', label: '% ownership', type: 'number' },
        { key: 'fully_diluted_pct', label: '% fully diluted', type: 'number' },
        {
          key: 'status',
          label: 'Estado',
          type: 'select',
          options: [
            { value: 'Activo', label: 'Activo' },
            { value: 'Inactivo', label: 'Inactivo' },
          ],
        },
        { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true },
      ],
    },
  ];

  const roundColumns = [
    { key: 'round_code', label: 'Código' },
    { key: 'name', label: 'Ronda' },
    { key: 'stage', label: 'Etapa' },
    { key: 'status', label: 'Estado' },
    { key: 'target_amount', label: 'Objetivo', render: (r) => formatMoney(r.target_amount) },
    { key: 'raised_amount', label: 'Levantado', render: (r) => formatMoney(r.raised_amount) },
    {
      key: 'investments_count',
      label: 'Inversiones',
      render: (r) => r.investments_count ?? 0,
    },
    {
      key: 'close_date',
      label: 'Cierre',
      render: (r) => (r.close_date ? String(r.close_date).slice(0, 10) : '—'),
    },
  ];

  const investmentColumns = [
    { key: 'investment_code', label: 'Código' },
    { key: 'investor_name', label: 'Inversionista' },
    {
      key: 'round_name',
      label: 'Ronda',
      render: (r) => r.round_name || r.round_stage || '—',
    },
    { key: 'amount', label: 'Monto', render: (r) => formatMoney(r.amount) },
    { key: 'instrument', label: 'Instrumento' },
    { key: 'ownership_pct', label: '%', render: (r) => formatPct(r.ownership_pct) },
    { key: 'status', label: 'Estado' },
    {
      key: 'investment_date',
      label: 'Fecha',
      render: (r) => (r.investment_date ? String(r.investment_date).slice(0, 10) : '—'),
    },
  ];

  const capColumns = [
    { key: 'entry_code', label: 'Código' },
    { key: 'shareholder_name', label: 'Accionista' },
    { key: 'investor_name', label: 'Inversionista', render: (r) => r.investor_name || '—' },
    { key: 'share_class', label: 'Clase' },
    {
      key: 'shares',
      label: 'Acciones',
      render: (r) =>
        Number(r.shares)
          ? Number(r.shares).toLocaleString('es-CL', { maximumFractionDigits: 4 })
          : '—',
    },
    { key: 'ownership_pct', label: '%', render: (r) => formatPct(r.ownership_pct) },
    { key: 'fully_diluted_pct', label: '% FD', render: (r) => formatPct(r.fully_diluted_pct) },
    { key: 'round_name', label: 'Ronda', render: (r) => r.round_name || '—' },
    { key: 'status', label: 'Estado' },
  ];

  const title =
    tab === 'rounds' ? 'Rondas' : tab === 'investments' ? 'Inversiones' : 'Cap table';
  const sections =
    tab === 'rounds' ? roundSections : tab === 'investments' ? investmentSections : capSections;
  const columns =
    tab === 'rounds' ? roundColumns : tab === 'investments' ? investmentColumns : capColumns;
  const rows =
    tab === 'rounds' ? filteredRounds : tab === 'investments' ? filteredInvestments : filteredCap;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          borderBottom: `1px solid ${ui.border}`,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 12, color: ui.muted }}>
          {tab === 'cap-table' && capSummary
            ? `${capSummary.entries || 0} entradas · ownership ${formatPct(capSummary.ownership_sum)} · FD ${formatPct(capSummary.fully_diluted_sum)}`
            : tab === 'rounds'
              ? `${filteredRounds.length} rondas`
              : `${filteredInvestments.length} inversiones`}
        </div>
        <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={btnPrimary} onClick={openCreate}>
            <Plus size={16} /> Nuevo
          </button>
          {selectedIds.length ? (
            <button
              type="button"
              style={{ ...btnSecondary(ui, isDarkMode), color: '#dc2626' }}
              onClick={bulkDelete}
            >
              <Trash2 size={16} /> Eliminar
            </button>
          ) : null}
        </div>
      </div>

      <SimpleTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={error}
        isDarkMode={isDarkMode}
        ui={ui}
        search={search}
        onSearch={setSearch}
        selectedIds={selectedIds}
        onToggleSelect={setSelectedIds}
        onRowClick={openEdit}
      />

      <FormDrawer
        isOpen={showForm}
        onClose={() => !saving && setShowForm(false)}
        onSave={save}
        isSaving={saving}
        isDarkMode={isDarkMode}
        title={editingId ? `Editar ${title.toLowerCase().replace(/s$/, '')}` : `Nueva ${title.toLowerCase().replace(/s$/, '')}`}
        formData={form}
        onChange={(key, value) => setForm((prev) => ({ ...prev, [key]: value }))}
        formError={formError}
        sections={sections}
        width={560}
        zIndex={10050}
      />
    </div>
  );
};

export default FinancingFundraisingViews;
